import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Flag, Plus, Trash2, Edit, Save, X, GripVertical,
  CheckCircle, Clock, ChevronRight, ChevronDown, Copy,
  FileText, Stethoscope, Pill, Activity, User, Calendar,
  AlertCircle, Settings, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_MILESTONE_ICONS = [
  { id: 'user', icon: User, name: 'Person' },
  { id: 'file', icon: FileText, name: 'Document' },
  { id: 'stethoscope', icon: Stethoscope, name: 'Medical' },
  { id: 'pill', icon: Pill, name: 'Treatment' },
  { id: 'activity', icon: Activity, name: 'Activity' },
  { id: 'flag', icon: Flag, name: 'Flag' },
  { id: 'calendar', icon: Calendar, name: 'Calendar' },
  { id: 'check', icon: CheckCircle, name: 'Check' },
];

const MilestoneTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    milestones: [
      { id: 'new1', name: 'Screening', icon: 'user', duration: '', description: '' },
      { id: 'new2', name: 'Completion', icon: 'flag', duration: '-', description: '' },
    ],
  });
  const [draggedMilestone, setDraggedMilestone] = useState(null);

  // Fetch templates from backend
  const fetchTemplates = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/registry/milestone-templates`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Map backend format to frontend format
        const mapped = data.templates.map(t => ({
          id: t.id,
          name: t.name,
          description: t.description,
          isDefault: t.is_default || false,
          milestones: t.milestones.map((m, idx) => ({
            id: m.id || `m${idx}`,
            name: m.name,
            icon: m.icon || 'flag',
            duration: m.duration || '',
            description: m.description || ''
          }))
        }));
        setTemplates(mapped);
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const saveNewTemplate = async () => {
    if (!newTemplate.name) {
      toast.error('Template name is required');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/registry/milestone-templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newTemplate.name,
          description: newTemplate.description,
          milestones: newTemplate.milestones
        })
      });
      
      if (response.ok) {
        toast.success('Template created successfully');
        setShowNewTemplate(false);
        setNewTemplate({
          name: '',
          description: '',
          milestones: [
            { id: 'new1', name: 'Screening', icon: 'user', duration: '', description: '' },
            { id: 'new2', name: 'Completion', icon: 'flag', duration: '-', description: '' },
          ],
        });
        fetchTemplates();
      } else {
        toast.error('Failed to create template');
      }
    } catch (err) {
      toast.error('Failed to create template');
    }
  };

  const deleteTemplate = async (templateId) => {
    try {
      const response = await fetch(`${API_URL}/api/registry/milestone-templates/${templateId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Template deleted');
        fetchTemplates();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to delete template');
      }
    } catch (err) {
      toast.error('Failed to delete template');
    }
  };

  const getIconComponent = (iconId) => {
    const iconDef = DEFAULT_MILESTONE_ICONS.find(i => i.id === iconId);
    return iconDef ? iconDef.icon : Flag;
  };

  const handleAddMilestone = (templateMilestones, setMilestones) => {
    const newMilestone = {
      id: `m${Date.now()}`,
      name: 'New Milestone',
      icon: 'check',
      duration: '',
      description: '',
    };
    // Insert before the last milestone (completion)
    const updated = [...templateMilestones];
    updated.splice(updated.length - 1, 0, newMilestone);
    setMilestones(updated);
  };

  const handleRemoveMilestone = (templateMilestones, setMilestones, milestoneId) => {
    if (templateMilestones.length <= 2) {
      toast.error('Template must have at least 2 milestones');
      return;
    }
    setMilestones(templateMilestones.filter(m => m.id !== milestoneId));
  };

  const handleDragStart = (e, milestone, index) => {
    setDraggedMilestone({ milestone, index });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetIndex, templateMilestones, setMilestones) => {
    e.preventDefault();
    if (!draggedMilestone) return;

    const updated = [...templateMilestones];
    const [removed] = updated.splice(draggedMilestone.index, 1);
    updated.splice(targetIndex, 0, removed);
    setMilestones(updated);
    setDraggedMilestone(null);
  };

  const handleSaveNewTemplate = () => {
    if (!newTemplate.name) {
      toast.error('Please enter a template name');
      return;
    }

    const template = {
      id: `custom_${Date.now()}`,
      name: newTemplate.name,
      description: newTemplate.description,
      isDefault: false,
      milestones: newTemplate.milestones,
    };

    setTemplates([...templates, template]);
    setNewTemplate({
      name: '',
      description: '',
      milestones: [
        { id: 'new1', name: 'Screening', icon: 'user', duration: '', description: '' },
        { id: 'new2', name: 'Completion', icon: 'flag', duration: '-', description: '' },
      ],
    });
    setShowNewTemplate(false);
    toast.success('Template created successfully');
  };

  const handleDuplicateTemplate = (template) => {
    const duplicate = {
      ...template,
      id: `custom_${Date.now()}`,
      name: `${template.name} (Copy)`,
      isDefault: false,
      milestones: template.milestones.map(m => ({ ...m, id: `m${Date.now()}_${m.id}` })),
    };
    setTemplates([...templates, duplicate]);
    toast.success('Template duplicated');
  };

  const handleDeleteTemplate = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template?.isDefault) {
      toast.error('Cannot delete default templates');
      return;
    }
    setTemplates(templates.filter(t => t.id !== templateId));
    if (selectedTemplate?.id === templateId) {
      setSelectedTemplate(null);
    }
    toast.success('Template deleted');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Milestone Templates</h2>
          <p className="text-sm text-gray-500">Create and manage custom milestone sequences for different study types</p>
        </div>
        <Button 
          className="bg-[#1877F2] hover:bg-[#166FE5]"
          onClick={() => setShowNewTemplate(true)}
        >
          <Plus className="w-4 h-4 mr-1" />
          Create Template
        </Button>
      </div>

      {/* New Template Form */}
      {showNewTemplate && (
        <Card className="border-0 shadow-sm border-l-4 border-l-[#1877F2]">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium text-gray-700">
                Create New Template
              </CardTitle>
              <button onClick={() => setShowNewTemplate(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Template Name</label>
                <Input
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                  placeholder="e.g., Long-term Follow-up Study"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Description</label>
                <Input
                  value={newTemplate.description}
                  onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                  placeholder="Brief description of when to use this template"
                />
              </div>
            </div>

            {/* Milestones Editor */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Milestones</label>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleAddMilestone(newTemplate.milestones, (m) => setNewTemplate({ ...newTemplate, milestones: m }))}
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Milestone
                </Button>
              </div>
              <div className="space-y-2">
                {newTemplate.milestones.map((milestone, index) => {
                  const IconComponent = getIconComponent(milestone.icon);
                  return (
                    <div 
                      key={milestone.id}
                      className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg"
                      draggable
                      onDragStart={(e) => handleDragStart(e, milestone, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDrop={(e) => handleDrop(e, index, newTemplate.milestones, (m) => setNewTemplate({ ...newTemplate, milestones: m }))}
                    >
                      <GripVertical className="w-4 h-4 text-gray-400 cursor-grab" />
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <IconComponent className="w-4 h-4 text-blue-600" />
                      </div>
                      <Input
                        value={milestone.name}
                        onChange={(e) => {
                          const updated = [...newTemplate.milestones];
                          updated[index].name = e.target.value;
                          setNewTemplate({ ...newTemplate, milestones: updated });
                        }}
                        className="flex-1"
                        placeholder="Milestone name"
                      />
                      <select
                        value={milestone.icon}
                        onChange={(e) => {
                          const updated = [...newTemplate.milestones];
                          updated[index].icon = e.target.value;
                          setNewTemplate({ ...newTemplate, milestones: updated });
                        }}
                        className="px-2 py-1 border rounded text-sm"
                      >
                        {DEFAULT_MILESTONE_ICONS.map(icon => (
                          <option key={icon.id} value={icon.id}>{icon.name}</option>
                        ))}
                      </select>
                      <Input
                        value={milestone.duration}
                        onChange={(e) => {
                          const updated = [...newTemplate.milestones];
                          updated[index].duration = e.target.value;
                          setNewTemplate({ ...newTemplate, milestones: updated });
                        }}
                        className="w-24"
                        placeholder="Duration"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMilestone(newTemplate.milestones, (m) => setNewTemplate({ ...newTemplate, milestones: m }), milestone.id)}
                        className="h-8 w-8 p-0"
                        disabled={newTemplate.milestones.length <= 2}
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowNewTemplate(false)}>Cancel</Button>
              <Button 
                className="bg-[#1877F2] hover:bg-[#166FE5]"
                onClick={handleSaveNewTemplate}
              >
                <Save className="w-4 h-4 mr-1" />
                Save Template
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Templates Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map((template) => (
          <Card 
            key={template.id}
            className={`border-0 shadow-sm cursor-pointer transition-all hover:shadow-md ${
              selectedTemplate?.id === template.id ? 'ring-2 ring-[#1877F2]' : ''
            }`}
            onClick={() => setSelectedTemplate(selectedTemplate?.id === template.id ? null : template)}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-gray-900">{template.name}</h3>
                    {template.isDefault && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">Default</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{template.description}</p>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDuplicateTemplate(template);
                    }}
                    title="Duplicate"
                  >
                    <Copy className="w-3 h-3 text-gray-400" />
                  </Button>
                  {!template.isDefault && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTemplate(template.id);
                      }}
                      title="Delete"
                    >
                      <Trash2 className="w-3 h-3 text-red-400" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Milestone Preview */}
              <div className="flex items-center gap-1 overflow-hidden">
                {template.milestones.slice(0, 5).map((milestone, idx) => {
                  const IconComponent = getIconComponent(milestone.icon);
                  return (
                    <div key={milestone.id} className="flex items-center">
                      <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center" title={milestone.name}>
                        <IconComponent className="w-3 h-3 text-gray-600" />
                      </div>
                      {idx < Math.min(template.milestones.length, 5) - 1 && (
                        <div className="w-3 h-0.5 bg-gray-200"></div>
                      )}
                    </div>
                  );
                })}
                {template.milestones.length > 5 && (
                  <span className="text-xs text-gray-400 ml-1">+{template.milestones.length - 5}</span>
                )}
              </div>

              <div className="mt-3 text-xs text-gray-500">
                {template.milestones.length} milestones
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Selected Template Details */}
      {selectedTemplate && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium text-gray-700 flex items-center">
                <Settings className="w-5 h-5 mr-2 text-[#1877F2]" />
                {selectedTemplate.name} - Milestone Sequence
              </CardTitle>
              <Button variant="outline" size="sm">
                <Copy className="w-4 h-4 mr-1" />
                Apply to Subject
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-6 top-4 bottom-4 w-0.5 bg-gray-200"></div>
              
              <div className="space-y-4">
                {selectedTemplate.milestones.map((milestone, idx) => {
                  const IconComponent = getIconComponent(milestone.icon);
                  return (
                    <div key={milestone.id} className="flex items-start gap-4 relative">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center z-10 border-2 border-white shadow">
                        <IconComponent className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1 pt-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">{milestone.name}</p>
                            <p className="text-sm text-gray-500">{milestone.description || 'No description'}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-600">
                              <Clock className="w-3 h-3 inline mr-1" />
                              {milestone.duration || 'Variable'}
                            </span>
                            <span className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-xs text-gray-600">
                              {idx + 1}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MilestoneTemplates;
